import React, { useState, useEffect } from 'react';
import './ProjectionsPage.css';
import logo from './assets/logo.png';

const API_BASE = process.env.REACT_APP_API_URL || '';

const ProjectionsPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'Projections | LapLeaders';
  }, []);

  const [races, setRaces] = useState([]);
  const [selectedRace, setSelectedRace] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState('cup');
  const [projections, setProjections] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [selectedLine, setSelectedLine] = useState('');
  const [overUnderResult, setOverUnderResult] = useState(null);
  const [overUnderError, setOverUnderError] = useState(null);
  const [overUnderLoading, setOverUnderLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [runType, setRunType] = useState('pre_weekend');

  // Fetch upcoming races on mount
  useEffect(() => {
    fetchUpcomingRaces();
  }, []);

  // Fetch projections when race or run type changes
  useEffect(() => {
    if (selectedRace) {
      fetchProjections(selectedRace, runType);
      fetchDrivers(selectedRace);
    }
  }, [selectedRace, runType]);

  const fetchUpcomingRaces = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/projections/upcoming-races?this_week_only=true`);
      if (response.ok) {
        const data = await response.json();
        setRaces(data);
        // Select first race of selected series
        const seriesRaces = data.filter(r => r.series_code === selectedSeries);
        if (seriesRaces.length > 0) {
          setSelectedRace(seriesRaces[0].race_id);
        } else if (data.length > 0) {
          // Fallback to first available race
          setSelectedRace(data[0].race_id);
          setSelectedSeries(data[0].series_code);
        }
      }
    } catch (err) {
      console.error('Error fetching races:', err);
    }
  };

  // Filter races by selected series
  const filteredRaces = races.filter(r => r.series_code === selectedSeries);

  // Handle series change
  const handleSeriesChange = (series) => {
    setSelectedSeries(series);
    const seriesRaces = races.filter(r => r.series_code === series);
    if (seriesRaces.length > 0) {
      setSelectedRace(seriesRaces[0].race_id);
    }
    // Reset state when series changes
    setProjections(null);
    setOverUnderResult(null);
    setSelectedDriver('');
    setSelectedLine('');
  };

  const fetchProjections = async (raceId, type) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/projections/races/${raceId}/projections?run_type=${type}`
      );
      if (response.ok) {
        const data = await response.json();
        setProjections(data);
      } else if (response.status === 404) {
        setProjections(null);
        setError('No projections available yet. Run a simulation first.');
      }
    } catch (err) {
      setError('Error loading projections');
      console.error(err);
    }
    setLoading(false);
  };

  const fetchDrivers = async (raceId) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/projections/races/${raceId}/drivers`
      );
      if (response.ok) {
        const data = await response.json();
        setDrivers(data);
      }
    } catch (err) {
      console.error('Error fetching drivers:', err);
    }
  };

  const calculateOverUnder = async () => {
    if (!selectedDriver || !selectedLine) return;

    setOverUnderLoading(true);
    setOverUnderError(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/v1/projections/races/${selectedRace}/over-under?driver_id=${selectedDriver}&line=${selectedLine}&run_type=${runType}`
      );
      if (response.ok) {
        const data = await response.json();
        setOverUnderResult(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setOverUnderError(errorData.detail || `Error: ${response.status}`);
        setOverUnderResult(null);
      }
    } catch (err) {
      console.error('Error calculating over/under:', err);
      setOverUnderError('Network error - please try again');
      setOverUnderResult(null);
    } finally {
      setOverUnderLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Backend returns UTC timestamps without 'Z' suffix, so append it
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    const date = new Date(utcTimestamp);
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }) + ' ET';
  };

  // Generate line options (1.5 to 35.5)
  const lineOptions = [];
  for (let i = 1; i <= 35; i++) {
    lineOptions.push(i + 0.5);
  }

  return (
    <div className="projections-page">
      {/* Header */}
      <header className="projections-header">
        <img src={logo} alt="LapLeaders" className="page-header-logo" />
        <h1>Race Projections</h1>
        <p className="subtitle">10,000 Data-Driven Simulations</p>
      </header>

      {/* Series Tabs */}
      <section className="series-tabs">
        <button
          className={`series-tab ${selectedSeries === 'cup' ? 'active' : ''}`}
          onClick={() => handleSeriesChange('cup')}
        >
          Cup Series
        </button>
        <button
          className={`series-tab ${selectedSeries === 'truck' ? 'active' : ''}`}
          onClick={() => handleSeriesChange('truck')}
        >
          Truck Series
        </button>
        <button
          className={`series-tab ${selectedSeries === 'xfinity' ? 'active' : ''}`}
          onClick={() => handleSeriesChange('xfinity')}
        >
          Xfinity Series
        </button>
      </section>

      {/* Race Selection */}
      <section className="race-selection">
        <div className="selection-row">
          <div className="select-group">
            <label>Select Race:</label>
            <select
              value={selectedRace || ''}
              onChange={(e) => setSelectedRace(Number(e.target.value))}
            >
              {filteredRaces.length > 0 ? (
                filteredRaces.map((race) => (
                  <option key={race.race_id} value={race.race_id}>
                    {race.race_name} @ {race.track_name}
                  </option>
                ))
              ) : (
                <option value="">No races this week</option>
              )}
            </select>
          </div>

          <div className="select-group">
            <label>Projection Type:</label>
            <select
              value={runType}
              onChange={(e) => setRunType(e.target.value)}
            >
              <option value="pre_weekend">Pre-Weekend</option>
              <option value="post_practice">Post P/Q</option>
            </select>
          </div>
        </div>

        {projections && (
          <div className="last-updated">
            <span className="update-label">Last Updated:</span>
            <span className="update-time">{formatTimestamp(projections.run_timestamp)}</span>
            <span className="season-phase">({projections.season_phase} season weights)</span>
          </div>
        )}
      </section>

      {/* Main Content Grid */}
      <div className="content-grid">
        {/* Driver Rankings Table */}
        <section className="rankings-section">
          <h2>Driver Rankings</h2>
          {loading && (
            <div className="loading">
              <div className="racing-spinner"></div>
              <span>Loading projections...</span>
            </div>
          )}
          {error && <div className="error-message">{error}</div>}

          {projections && (
            <div className="table-container">
              <table className="rankings-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Start</th>
                    <th>Driver</th>
                    <th>Score</th>
                    <th>Avg Fin</th>
                    <th>Win %</th>
                    <th>Top 5 %</th>
                    <th>Top 10 %</th>
                  </tr>
                </thead>
                <tbody>
                  {projections.results.map((driver, index) => (
                    <tr key={driver.driver_id} className={index < 5 ? 'top-5' : ''}>
                      <td className="rank">{index + 1}</td>
                      <td className="start-pos">{driver.start_position ? `P${driver.start_position}` : '-'}</td>
                      <td className="driver-name">{driver.driver_name}</td>
                      <td className="composite-score">{driver.composite_score ? driver.composite_score.toFixed(1) : '-'}</td>
                      <td className="avg-finish">{driver.avg_finish.toFixed(2)}</td>
                      <td className="win-prob">{driver.win_probability.toFixed(1)}%</td>
                      <td className="top5-prob">{driver.top5_probability.toFixed(1)}%</td>
                      <td className="top10-prob">{driver.top10_probability.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Over/Under Calculator */}
        <section className="over-under-section">
          <h2>Over/Under Calculator</h2>
          <div className="calculator-card">
            <div className="calculator-inputs">
              <div className="input-group">
                <label>Select Driver:</label>
                <select
                  value={selectedDriver}
                  onChange={(e) => {
                    setSelectedDriver(e.target.value);
                    setOverUnderResult(null);
                    setOverUnderError(null);
                  }}
                >
                  <option value="">-- Select Driver --</option>
                  {drivers.map((driver) => (
                    <option key={driver.driver_id} value={driver.driver_id}>
                      {driver.driver_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="input-group">
                <label>Finishing Position Line:</label>
                <select
                  value={selectedLine}
                  onChange={(e) => {
                    setSelectedLine(e.target.value);
                    setOverUnderResult(null);
                    setOverUnderError(null);
                  }}
                >
                  <option value="">-- Select Line --</option>
                  {lineOptions.map((line) => (
                    <option key={line} value={line}>
                      {line}
                    </option>
                  ))}
                </select>
              </div>

              <button
                className="calculate-btn"
                onClick={calculateOverUnder}
                disabled={!selectedDriver || !selectedLine || overUnderLoading}
              >
                {overUnderLoading ? 'Calculating...' : 'Calculate'}
              </button>
            </div>

            {overUnderError && (
              <div className="error-message">{overUnderError}</div>
            )}

            {overUnderResult && (
              <div className="result-card">
                <h3>{overUnderResult.driver_name}</h3>
                <div className="result-line">Line: {overUnderResult.line}</div>
                <div className="result-stats">
                  <span>Avg Finish: {overUnderResult.avg_finish.toFixed(2)}</span>
                  {overUnderResult.median_finish && (
                    <span>Median: {overUnderResult.median_finish}</span>
                  )}
                  {overUnderResult.dnf_probability != null && (
                    <span>DNF Risk: {overUnderResult.dnf_probability.toFixed(1)}%</span>
                  )}
                </div>

                <div className="probability-bars">
                  <div className="prob-row under">
                    <span className="prob-label">UNDER {overUnderResult.line}</span>
                    <div className="prob-bar-container">
                      <div
                        className="prob-bar"
                        style={{ width: `${overUnderResult.under_probability}%` }}
                      ></div>
                    </div>
                    <span className="prob-value">
                      {overUnderResult.under_probability}%
                      {overUnderResult.implied_under_odds != null && (
                        <span className="implied-odds">
                          ({overUnderResult.implied_under_odds > 0 ? '+' : ''}{overUnderResult.implied_under_odds})
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="prob-row over">
                    <span className="prob-label">OVER {overUnderResult.line}</span>
                    <div className="prob-bar-container">
                      <div
                        className="prob-bar"
                        style={{ width: `${overUnderResult.over_probability}%` }}
                      ></div>
                    </div>
                    <span className="prob-value">
                      {overUnderResult.over_probability}%
                      {overUnderResult.implied_over_odds != null && (
                        <span className="implied-odds">
                          ({overUnderResult.implied_over_odds > 0 ? '+' : ''}{overUnderResult.implied_over_odds})
                        </span>
                      )}
                    </span>
                  </div>

                  {overUnderResult.push_probability > 0 && (
                    <div className="prob-row push">
                      <span className="prob-label">PUSH</span>
                      <div className="prob-bar-container">
                        <div
                          className="prob-bar"
                          style={{ width: `${overUnderResult.push_probability}%` }}
                        ></div>
                      </div>
                      <span className="prob-value">{overUnderResult.push_probability}%</span>
                    </div>
                  )}
                </div>

                <div className={`edge-indicator ${overUnderResult.edge.toLowerCase()}`}>
                  <span className="edge-direction">{overUnderResult.edge}</span>
                  <span className="edge-size">+{Math.abs(overUnderResult.edge_size).toFixed(1)}% edge</span>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="projections-footer">
        <p>Projections based on {projections?.num_simulations?.toLocaleString() || '10,000'} race simulations</p>
        <p>Track: {projections?.track_name} | Season Phase: {projections?.season_phase}</p>
      </footer>
    </div>
  );
};

export default ProjectionsPage;
